import { Component, OnInit,ViewChild ,AfterViewInit, Output, EventEmitter} from '@angular/core';
import {MatTable} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import { Feature, Point, random } from '@turf/turf';
import { FeatureCollection, Geometry, Polygon } from 'geojson';
import { MapPainterService } from '../services/map-painter/map-painter.service';
import { randomInt } from 'd3';
import { MathAux } from '../services/math-aux/math-aux';
import { ServiceService } from '../services/service.service';
import { Observable } from 'rxjs';
import { Tempo } from '../models/tempo';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { MatSpinner } from '@angular/material/progress-spinner';
import { finalize } from 'rxjs/operators';
import { DatelistComponent } from '../datelist/datelist.component';
import { CSVAux } from '../services/csv-aux/csv-aux';
import { TimespanService } from '../services/timespan/timespan.service';
import { TimeFormatter } from '../utils/time-formatter';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RainPatternsResponse } from '../models/rain-patterns-response';

/**
 * Este componente é uma tabela na qual as linhas correspondem aos nomes das
 * estações ou bairros, e as colunas correspondem aos acumulados de chuva de
 * 15 minutos, 30 minutos, 1, 6, 12, 24, 48, 72, 96 horas, e 1 mês (30 dias).
 * Os nomes das estações e as colunas são clicáveis e atualizam o mapa.
 */
@Component({
  selector: 'app-heatmatrix',
  templateUrl: './heatmatrix.component.html',
  styleUrls: ['./heatmatrix.component.css']
})
export class HeatmatrixComponent implements OnInit , AfterViewInit{
  displayedArray: Feature<Point|Polygon>[] = [];

  stationArray: Feature<Point>[] = [];
  neighborhoodArray: Feature<Point>[] = [];
  activeRegions: Feature<Polygon|Point>[][] = [];

  displayState = 1;

  displayedColumns: string [] = [];
  timeColumns: {name: string, text: string, timeVal: number,color:string,selected:boolean}[] = [
    { name: 'quinzemin', text: '15m', timeVal: 15,color:'quinzemincolor',selected: false},
    { name: 'trintamin', text: '30m', timeVal: 30,color:'trintamincolor',selected: false},
    { name: 'umahora', text: '1h', timeVal: 60,color:'umahoracolor',selected: false},
    { name: 'seishoras', text: '6h', timeVal: 360,color:'seishorascolor',selected: false},
    { name: 'dozehoras', text: '12h', timeVal: 720,color:'dozehorascolor',selected: false},
    { name: 'vintequatrohoras', text: '24h', timeVal: 1440,color:'vintequatrohorascolor',selected: false},
    { name: 'quarentaoitohoras', text: '48h', timeVal: 2880,color:'quarentaoitohorascolor',selected: false},
    { name: 'setentaduashoras', text: '72h', timeVal: 4320,color:'setentaduashorascolor',selected: false},
    { name: 'noventaseishoras', text: '96h', timeVal: 5760,color:'noventaseishorascolor',selected: false},
    { name: 'mes', text: '1 mês', timeVal: 43200,color:'mescolor',selected: false}
  ];
  dataSource = new MatTableDataSource(this.displayedArray);
  clickedRows = new Set<Feature<GeoJSON.Point>>();
  sortMode = 1
  heatmatrixClass = "heatmatrix-hidden"
  button_visibility = "heatmatrix-ctrl-button visible"

  selectedId = -1

  selectedNeighborhood = ""

  displayName = "Estações"

  lastDateListSearched = -1
  returnTempoId = -1
  actualTempoId = -1
  backdrop = this.createBackdrop();

  /** Observable dos horários com padrões das intensidades das chuvas iguais. */
  Tempos$ : Observable<any>
  datelist_show = false;
  tempo:Tempo[];

  isFetchRunning = false;

  rainPatternsResponse: RainPatternsResponse;

  @ViewChild(DatelistComponent) dateList: DatelistComponent;

  @Output() timespanClicked = new EventEmitter<string>();
  @Output() rowClicked = new EventEmitter<Feature<GeoJSON.Point|Polygon>>();
  @Output() tableClosed = new EventEmitter<boolean>();
  @Output() dateListFound = new EventEmitter<[string,Tempo[]]>(); 
  @Output() searchDate = new EventEmitter<Tempo[]>();
  @Output() changeTableDisplay = new EventEmitter<Feature<Polygon|Point>[][]>();
  @Output() dragDisabled = new EventEmitter<boolean>();
  @Output() rainPatternEvents = new EventEmitter<FeatureCollection>();

  @ViewChild(MatTable) table: MatTable<any>;

  @ViewChild(MatSort) sort: MatSort;
    ngAfterViewInit() {
      this.dataSource.sort = this.sort;
    }

  constructor(
    private service: ServiceService,
    private overlay: Overlay, 
    private snackBar: MatSnackBar) {
      
    // inicializa o array dos nomes de referência das colunas do componente tabela.
    this.displayedColumns = ['station'].concat(this.getTimeColumnsNames());
  } 

  ngOnInit(): void {
  }

  /**
   * Alterna entre exibir e esconder a heatmatrix.
   */
  changeView(){
    if (this.heatmatrixClass == "heatmatrix-hidden"){
        this.heatmatrixClass = "heatmatrix-show"
    } else {
      this.close();
    }
  }

  /**
   * Esconde a heatmatrix.
   */
  close() {
    this.heatmatrixClass = "heatmatrix-hidden"
    this.tableClosed.emit();
  }

  /**
   * Reinicia a heatmatrix
   * 
   * @param newSearch indica se uma consulta em um instante de tempo diferente foi disparada.
   * @param tempo tempo selecionado.
   * @param selectedTimestamp nome da coluna de acumulado de chuva selecionada.
   */
  reset(newSearch: boolean, tempo: Tempo[], selectedTimestamp: string) {

    if (newSearch){
      this.resetLastDate();
    }
    this.actualTempoId = tempo[0].id_tempo;
    this.activateColumn(selectedTimestamp);
    this.tempo = tempo;
    this.activeRegions = [];
  }

  /**
   * Atualiza a heatmatrix com novos dados das estações e dos bairros.
   * @param stations features dos pontos das estações.
   * @param neighborhoods features dos centroides dos bairros.
   */
  updateTable(stations: Feature<GeoJSON.Point>[], neighborhoods:Feature<GeoJSON.Point>[]){
    this.stationArray =  stations.filter(function(value){ 
      return value.properties.indice_pluv >= 0;
    });
    this.neighborhoodArray = neighborhoods;
    if (this.displayState == 1) this.displayedArray = this.stationArray;
    else this.displayedArray = this.neighborhoodArray;
    this.table.renderRows()
  }

  /**
   * Trata o click no nome de uma estação ou bairro, destacando a geometria correspondente no mapa.
   * @param row linha clicada.
   */
  onRowClicked(row:Feature<GeoJSON.Point | Polygon>){
    if (!row.properties.region){
      if(row.properties.neighborhood){
       if (this.selectedNeighborhood != row.properties.nome_estacao_original) {
        this.selectedNeighborhood = row.properties.nome_estacao_original
        this.rowClicked.emit(row)
       } else {
        this.selectedNeighborhood = ""
        this.rowClicked.emit({ "type": "Feature", "properties": {"neighborhood":true,"erase":true}, "geometry": {"type":"Point","coordinates":[]} });
       }
      } else {
        if (this.selectedId != row.properties.id){
          this.selectedId = row.properties.id
          this.rowClicked.emit(row)
        } else {
          this.selectedId = -1
          this.rowClicked.emit({ "type": "Feature", "properties": {"erase":true}, "geometry": {"type":"Point","coordinates":[]} });
        }
      }
    }
  }

  /**
   * Trata o click em uma coluna, disparando um evento para atualizar o mapa usando
   * o acumulado de chuva clicado.
   * @param timespan 
   */
  onTimespanClicked(timespan:string){
    this.timeColumns.forEach( (val) =>{
      if (val.name == timespan)
        val.selected = true;
      else
        val.selected = false
    })
    this.timespanClicked.emit(timespan)
  }

  /**
   * Obtém um array com os nomes das colunas dos tempos.
   * Usado para inicializar o array displayedColumns, que será usado no componente tabela do Material.
   * @returns nomes das colunas, usados como referências no componente tabela.
   */
  getTimeColumnsNames(): string[] {
    return this.timeColumns.map(s => s.name);
  }

  /**
   * Chama método auxiliar para arredondar o número de casas decimais de um número
   * 
   * @param val número decimal
   * @param decimalPlaces número de casas decimais que serão mantidas
   * @returns número com <decimalPlaces> casas decimais
   */
  round(val: number, decimalPlaces: number): number {
    return MathAux.round(val, decimalPlaces);
  }

  /**
   * busca outras datas com o mesmo padrão discreto selecionado
   * 
   * @param station estação 
   */
  patternSearch(station:Feature<GeoJSON.Point>){
    if (this.returnTempoId == this.actualTempoId || this.returnTempoId == -1){
      
      // this.showBackdrop();

      this.isFetchRunning = true;

      this.lastDateListSearched = station.properties.id_estacao;
      this.dateList.last_date_id = station.properties.id_estacao;
      this.returnTempoId = station.properties.id_tempo

      let id_tempo = station.properties.id_tempo;
      let id = station.properties.id_estacao;
      let discreteTypes: {[key: string]: string} = {};

      this.timeColumns.forEach( (val)=>{
        discreteTypes[val.name] = this.getDiscreteValue(station, val.name);
      });

      this.Tempos$ = this.service.getTimeByPattern(id_tempo, id, discreteTypes);

      this.Tempos$.pipe(finalize(() => {
        // this.hideBackdrop();
        this.isFetchRunning = false;
      })).subscribe( (val)=>{
        this.rainPatternsResponse = val;
        this.updateDateList([station.properties.nome_estacao_original,val.tempos]);
        this.rainPatternEvents.emit(val.ocorrencias);
        this.snackBar.open("Registros de padrões de chuvas encontrados", null, {duration: 6000})
        
        // exibe a date list
        if (this.heatmatrixClass == "heatmatrix-show") {
          this.datelist_show = true;
          this.dateList.visible = true;
        }
      })
    }
  }

  /**
   * Encaminha o evento de ligar / desligar a camada do heatmap das ocorrências para o componente pai.
   * @returns 
   */
  forwardEventsHeatmapLayerToggled() {
    
    if (this.rainPatternsResponse == null) {
      return;
    }

    this.rainPatternEvents.emit(this.rainPatternsResponse.ocorrencias);
  }
  
  /**
   * Retorna o estado de discreção de uma estação em um determinado intervalo de tempo
   * 
   * @param station estação 
   * @param timestamp intervalo de tempo que deseja 
   * @returns valor da discreção
   */
  getDiscreteValue(station: Feature<GeoJSON.Point>,timestamp: string):string{

    switch (station.properties.color[timestamp]) {
      case '#339944':
        return "fraca"
      case  '#FFCC00':
        return "moderada"
      case  '#FF8822':
        return "forte"
      default:
        return "muitoforte"
    }
  }


  private createBackdrop() {
    return this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'dark-backdrop',
      positionStrategy: this.overlay.position()
        .global()
        .centerHorizontally()
        .centerVertically()
    })
  }
  /**
     * Exibe o backdrop com um spinner centraliado
     */
   private showBackdrop() {
    this.backdrop.attach(new ComponentPortal(MatSpinner));
  }

  /**
   * Esconde o backdrop com o spinner
   */
  private hideBackdrop() {
    this.backdrop.detach();
  }

  /**
   * Alterna entre exibir e esconder a date list
   */
  switchDatelist(){
    this.datelist_show = !this.datelist_show
    this.dateList.changeView()
  }

  /**
   * Configura a última data selecionada na date list
   * 
   * @param tempo último tempo selecionado
   */
  setDateListLastDate(tempo: Tempo) {
    this.dateList.lastDate = tempo;
  }

  /**
   * 
   * @param tempo 
   */
  setDateListLastEndDate(tempo: Tempo) {
    this.dateList.lastEndDate = tempo;
  }

  /**
   * Atualiza os dados da date list
   * 
   * @param result par nome da estação selecionado e array de tempos em que ocorreram 
   *    padrões de chuva semelhantes.
   */
  updateDateList(result: [string, any[]]) {
    this.dateList.updateList(result);
  }

  /**
   * Limpa os valores da date list.
   */
  eraseDateList(){
    this.dateList.erase();
  }

  /**
   * Encaminha o evento searchDate da date list para o componente que abriga a heat matrix.
   * 
   * @param tempo tempo selecionado para busca.
   */
  forwardSearchDate(dates: any[]) {
    let date = dates[0]
    this.lastDateListSearched = date.id_estacao
    let dateObject : Tempo = {ano:date.ano,dia:date.dia,mes:date.mes,hora:date.hora,id_tempo:date.id_tempo,minuto:date.minuto}
    this.searchDate.emit([dateObject, dates[1]]);
  }

  /**
   * Obtém a classe que determina se a date list deve ser exibida ou não.
   * 
   * @returns classe visible, se a heatmatrix estiver aberta e a flag de exibir a date list for true;
   *    hidden, caso contrário.
   */
  getDateListCSSClass(): string {

    return this.heatmatrixClass == 'heatmatrix-show' && this.datelist_show ? 'visible' : 'hidden'
  }

  /**
   * Obtém a cor que o botão de busca deveria assumir agora
   * 
   * @param value Feature equivalente ao botão de busca
   * @returns A cor que o botão de busca deveria assumir agora
   */
  getPSBBackgroundColor(value : Feature<Point>){

    let cinzaClaro = "#bccfd4";
    let nenhumBotaoClicado = this.returnTempoId == this.actualTempoId || this.returnTempoId == -1;
    if (nenhumBotaoClicado) return cinzaClaro;
    
    let azulEscuro = "#045174";
    let umBotaoClicado = this.lastDateListSearched == value.properties.id_estacao
    if (umBotaoClicado) return azulEscuro;
    
    // quando um botão é clicado, os botões de todas as outras linhas ficam travados
    let cinzaEscuro = "#778899";
    return cinzaEscuro;
  }

  /**
   * Obtém a cor que a estação deveria assumir agora
   * 
   * @param value Feature equivalente a estação
   * @returns A cor que a estação deveria assumir agora
   */
  getStationColor(value : Feature<Point|Polygon>){

    let verdeClaro = "#85a897";
    let linhaRegiao = value.properties.region;
    if (linhaRegiao) return verdeClaro;
    
    let roxo = "#ae86c4";
    let linhaBairroNaoClicada = value.properties.neighborhood && value.properties.nome_estacao_original != this.selectedNeighborhood;
    if (linhaBairroNaoClicada) return roxo;
    
    let cinzaEscuro = "#6d7274";
    let linhaBairroClicada = value.properties.neighborhood && value.properties.nome_estacao_original == this.selectedNeighborhood;
    if (linhaBairroClicada) return cinzaEscuro;
    
    let linhaEstacaoClicada = value.properties.id === this.selectedId;
    if(linhaEstacaoClicada) return cinzaEscuro;
    
    let azulClaro =  "#bccfd4";
    return azulClaro;
  }

  /**
   * Adiciona região poligonal desenhada pelo usuário à heatmatrix.
   * @param region região a ser adicionada.
   */
  addRegionToTable(region:Feature<Polygon|Point>[]){
    
    this.activeRegions.push(region)

    let regionToBeInserted = this.displayState == 1 ? region[0] : region[1];
    let newArray: Feature<Point|Polygon>[] = [regionToBeInserted];
    newArray.push(...this.displayedArray);
    this.displayedArray = newArray;

    this.table.renderRows();
  }

  /**
   * Atualiza a entrada de uma região poligonal na heatmatrix. Isso é necessário,
   * pois quando uma região poligonal é movida pelo mapa, seus valores interpolados
   * são atualizados.
   * @param region região a ser atualizada.
   */
  updateRegionFromTable(region:Feature<Polygon|Point>[]){
    let daIndex = this.displayedArray.findIndex( val =>{
      return val.id == region[0].id
    })
    if(this.displayState == 1) this.displayedArray[daIndex] = region[0]
    if(this.displayState == 2) this.displayedArray[daIndex] = region[1]

    let arIndex = this.activeRegions.findIndex( val =>{
      return val[0].id == region[0].id
    })
    this.activeRegions[arIndex] = region
    this.table.renderRows()
  }

  /**
   * Remove entrada da região poligonal da heatmatrix.
   * @param region região a ser removida.
   */
  removeRegionFromTable(region:Feature<Point|Polygon>[]){
    let daIndex = this.displayedArray.findIndex( val =>{
      return val.id == region[0].id
    })
    this.displayedArray.splice(daIndex,1)
    let arIndex = this.activeRegions.findIndex( val =>{
      return val[0].id == region[0].id
    })
    this.activeRegions.splice(arIndex,1)
    this.table.renderRows()
  }

  /**
   * Altera a heatmatrix para exibir os dados das estações.
   */
  displayStations(){

    let newArray = this.activeRegions.map( val => {return  val[0]}) as Feature<Polygon|Point>[];

    this.displayState = 1
    this.displayedArray = newArray.concat(this.stationArray)
    this.displayName = "Estações"
    this.changeTableDisplay.emit(this.activeRegions)
    this.table.renderRows()
  }

  /**
   * Altera a heatmatrix para exibir os dados interpolados nos centroides dos bairros.
   */
  displayNeighborhoods(){

    let newArray = this.activeRegions.map( val => {return  val[1]}) as Feature<Polygon|Point>[];

    this.displayState = 2
    this.displayedArray = newArray.concat(this.neighborhoodArray);
    this.displayName = "Bairros"
    this.changeTableDisplay.emit(this.activeRegions)
    this.table.renderRows()
  }

  /**
   * Gera o csv da heatmatrix com os dados exibidos (estações ou bairros).
   */
  generateCSV(){
    if (this.displayedArray.length > 0){
      let filename = `heatmatrix-${TimeFormatter.toLocaleString(this.tempo[0])}.csv`
      CSVAux.createCSVtableFromProperties(this.timeColumns,this.displayedArray,this.displayName,filename)
    }
  }

  /**
   * Remove a identificação da última data selecionada na datelist.
   */
  resetLastDate() {
    this.lastDateListSearched = -1;
    this.returnTempoId = -1;
  }

  /**
   * Marca uma coluna clicada como ativa.
   */
  activateColumn(selectedTimestamp: string) {

    // Primeiro, desabilita todas as colunas
    this.timeColumns.forEach( (val)=>{
      val.selected = false
    });

    // Depois, marca a coluna selecionada
    const filtered = this.timeColumns.filter(val => val.name == selectedTimestamp);
    if (filtered == null || filtered.length == 0) {
      return;
    }
    filtered[0].selected = true;
  }

  /**
   * Emite o evento que indica se o componente pode ser arrastado ou não.
   * @param disabled indica se o drag n drop do componente está desabilidado ou não.
   */
  emitDragDisabled(disabled: boolean) {
    this.dragDisabled.emit(disabled);
  }
  
}


